import { Box, Flex, Text, useBreakpointValue } from '@chakra-ui/react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

type IManuscriptsReceivedResponse = {
  by_revision: Array<{
    revision: string;
    count: number;
  }>;
};

interface Props {
  data: IManuscriptsReceivedResponse;
  axes?: {
    x?: string;
    y?: string;
  };
}

const BAR_COLORS = [
  '#2563EB',
  '#0F766E',
  '#D97706',
  '#9333EA',
  '#DC2626',
  '#0891B2',
  '#4F46E5',
  '#65A30D'
];

export const ManuscriptsReceivedChart = ({ data, axes }: Props) => {
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;
  const xAxisHeight = isMobile ? 44 : 30;
  const chartHeight = isMobile ? (axes?.x ? 248 : 272) : axes?.x ? 276 : 300;

  return (
    <Box>
      <Flex align="stretch">
        {axes?.y ? (
          <Flex align="center" justify="center" pr={2} minW="28px">
            <Text
              textStyle="textContext"
              textAlign="center"
              sx={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
            >
              {axes.y}
            </Text>
          </Flex>
        ) : null}
        <Box flex="1">
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart
              data={data.by_revision}
              margin={{
                top: 24,
                right: isMobile ? 8 : 16,
                left: isMobile ? 0 : 16,
                bottom: isMobile ? 12 : 8
              }}
            >
              <CartesianGrid
                strokeDasharray="4 4"
                stroke="#D9E2EC"
                vertical={false}
              />
              <XAxis
                dataKey="revision"
                axisLine={false}
                tickLine={false}
                height={xAxisHeight}
                interval={0}
                tickMargin={isMobile ? 10 : 6}
                tick={{ fill: '#334155', fontSize: isMobile ? 10 : 12 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                width={isMobile ? 32 : 40}
                tick={{ fill: '#334155', fontSize: isMobile ? 10 : 12 }}
              />
              <Tooltip
                cursor={{ fill: '#E2E8F0' }}
                contentStyle={{
                  borderRadius: 12,
                  border: '1px solid #CBD5E1',
                  boxShadow: '0 12px 28px rgba(15, 23, 42, 0.14)',
                  backgroundColor: '#FFFFFF'
                }}
                formatter={value => [`${value} manuscripts`, 'Count']}
              />
              <Bar
                dataKey="count"
                radius={[10, 10, 0, 0]}
                maxBarSize={48}
                isAnimationActive
                animationDuration={1100}
                animationEasing="ease-out"
                activeBar={{
                  fill: '#1D4ED8',
                  stroke: '#1E3A8A',
                  strokeWidth: 1
                }}
              >
                {data.by_revision.map((item, index) => (
                  <Cell
                    key={`${item.revision}-${item.count}`}
                    fill={BAR_COLORS[index % BAR_COLORS.length]}
                  />
                ))}
                <LabelList
                  dataKey="count"
                  position="top"
                  fill="#0F172A"
                  fontSize={isMobile ? 10 : 12}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Flex>
      {axes?.x ? (
        <Text mt={2} textAlign="center" textStyle="textContext">
          {axes.x}
        </Text>
      ) : null}
    </Box>
  );
};
